import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import { Search, User } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { LoadingState, ErrorState } from '@/components/ui/loading'
import { api, queryKeys, type Character } from '@/lib/api'
import { cn, getClassColor } from '@/lib/utils'

interface CharacterSearchProps {
  onCharacterSelect: (character: Character) => void
  className?: string
}

const REGIONS = [
  { value: 'us', label: 'US' },
  { value: 'eu', label: 'EU' },
  { value: 'kr', label: 'KR' },
  { value: 'tw', label: 'TW' },
  { value: 'cn', label: 'CN' },
]

export const CharacterSearch: React.FC<CharacterSearchProps> = ({
  onCharacterSelect,
  className,
}) => {
  const [characterName, setCharacterName] = React.useState('')
  const [realm, setRealm] = React.useState('')
  const [region, setRegion] = React.useState('us')
  const [searchTriggered, setSearchTriggered] = React.useState(false)

  const {
    data: character,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: queryKeys.character(characterName, realm, region),
    queryFn: () => api.searchCharacter(characterName, realm, region),
    enabled: searchTriggered && !!characterName.trim() && !!realm.trim(),
  })

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (characterName.trim() && realm.trim()) {
      setSearchTriggered(true)
      refetch()
    }
  }

  const handleCharacterSelect = () => {
    if (character) {
      onCharacterSelect(character)
    }
  }

  return (
    <Card className={cn('w-full max-w-2xl', className)}>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Search className="h-5 w-5" />
          <span>Character Search</span>
        </CardTitle>
        <CardDescription>
          Search for a character to begin analysis. Enter the character name, realm, and region.
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-6">
        <form onSubmit={handleSearch} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <label htmlFor="character-name" className="text-sm font-medium">
                Character Name
              </label>
              <Input
                id="character-name"
                placeholder="e.g. Thrall"
                value={characterName}
                onChange={(e) => setCharacterName(e.target.value)}
                required
              />
            </div>
            
            <div className="space-y-2">
              <label htmlFor="realm" className="text-sm font-medium">
                Realm
              </label>
              <Input
                id="realm"
                placeholder="e.g. Mal'Ganis"
                value={realm}
                onChange={(e) => setRealm(e.target.value)}
                required
              />
            </div>
            
            <div className="space-y-2">
              <label htmlFor="region" className="text-sm font-medium">
                Region
              </label>
              <Select
                id="region"
                value={region}
                onChange={(e) => setRegion(e.target.value)}
              >
                {REGIONS.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </Select>
            </div>
          </div>
          
          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? 'Searching...' : 'Search Character'}
          </Button>
        </form>

        {isLoading && <LoadingState>Searching for character...</LoadingState>}
        
        {error && (
          <ErrorState
            title="Character Not Found"
            message={error.message}
            onRetry={() => refetch()}
          />
        )}

        {character && (
          <Card className="border-2 border-primary/20 bg-primary/5">
            <CardContent className="p-4">
              <div className="flex items-center space-x-4">
                <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center">
                  {character.avatar ? (
                    <img 
                      src={character.avatar} 
                      alt={character.name}
                      className="h-16 w-16 rounded-full"
                    />
                  ) : (
                    <User className="h-8 w-8 text-muted-foreground" />
                  )}
                </div>
                
                <div className="flex-1">
                  <h3 
                    className="text-lg font-semibold"
                    style={{ color: getClassColor(character.class) }}
                  >
                    {character.name}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Level {character.level} {character.class}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {character.realm} - {character.region.toUpperCase()}
                  </p>
                  {character.guild && (
                    <p className="text-sm text-muted-foreground">
                      &lt;{character.guild.name}&gt;
                    </p>
                  )}
                </div>
                
                <Button onClick={handleCharacterSelect}>
                  Select Character
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </CardContent>
    </Card>
  )
}